import { useEffect, useMemo, useState } from 'react'

// Picker emoji completo stile WhatsApp (portato da gunsi-tally, resa nativa):
// tab per categoria + ricerca sul dataset @emoji-mart/data, lazy-loaded al
// primo mount così non pesa sul bundle iniziale.

const CATEGORY_META = {
  people:    { icon: '😀', label: 'Smileys & People' },
  nature:    { icon: '🐶', label: 'Nature' },
  foods:     { icon: '🍔', label: 'Food & Drink' },
  activity:  { icon: '⚽', label: 'Activities' },
  places:    { icon: '🚗', label: 'Travel & Places' },
  objects:   { icon: '💡', label: 'Objects' },
  symbols:   { icon: '🔣', label: 'Symbols' },
  flags:     { icon: '🏁', label: 'Flags' },
}

// `quick`: emoji suggerite mostrate in cima (a query vuota), prima del
// dataset completo — usate dal form spesa per le categorie tipiche.
export function EmojiPicker({ value, onChange, searchPlaceholder = 'Search…', quick = [] }) {
  const [data, setData]     = useState(null)
  const [activeCat, setCat] = useState('people')
  const [query, setQuery]   = useState('')

  useEffect(() => {
    let alive = true
    import('@emoji-mart/data').then(mod => {
      if (alive) setData(mod.default ?? mod)
    })
    return () => { alive = false }
  }, [])

  const items = useMemo(() => {
    if (!data) return []
    const q = query.trim().toLowerCase()
    if (q) {
      const out = []
      for (const id in data.emojis) {
        const em = data.emojis[id]
        const matches =
          em.name.toLowerCase().includes(q) ||
          em.keywords?.some(k => k.includes(q)) ||
          em.id.includes(q)
        if (matches) {
          const native = em.skins?.[0]?.native
          if (native) out.push(native)
        }
        if (out.length >= 200) break
      }
      return out
    }
    const cat = data.categories.find(c => c.id === activeCat)
    if (!cat) return []
    return cat.emojis
      .map(id => data.emojis[id]?.skins?.[0]?.native)
      .filter(Boolean)
  }, [data, activeCat, query])

  const categories = data?.categories?.filter(c => CATEGORY_META[c.id]) ?? []

  return (
    <div className="empk">
      <input
        type="search"
        className="cs-search"
        placeholder={searchPlaceholder}
        value={query}
        onChange={e => setQuery(e.target.value)}
      />

      {!query && quick.length > 0 && (
        <>
          <div className="empk-grid empk-quick">
            {quick.map(em => (
              <button
                key={'q-' + em}
                type="button"
                className={`empk-cell${value === em ? ' selected' : ''}`}
                onClick={() => onChange(em)}
                aria-label={em}
              >{em}</button>
            ))}
          </div>
          <div className="cs-separator" aria-hidden="true" />
        </>
      )}

      {data && !query && (
        <div className="empk-tabs">
          {categories.map(c => (
            <button
              key={c.id}
              type="button"
              className={`empk-tab${activeCat === c.id ? ' active' : ''}`}
              onClick={() => setCat(c.id)}
              aria-label={CATEGORY_META[c.id].label}
              title={CATEGORY_META[c.id].label}
            >{CATEGORY_META[c.id].icon}</button>
          ))}
        </div>
      )}

      <div className="empk-grid">
        {!data && <div className="loading-spinner small" />}
        {data && items.length === 0 && <span className="empk-empty">—</span>}
        {items.map(em => (
          <button
            key={em}
            type="button"
            className={`empk-cell${value === em ? ' selected' : ''}`}
            onClick={() => onChange(em)}
            aria-label={em}
          >{em}</button>
        ))}
      </div>
    </div>
  )
}
